import {
  type IncidentReport,
  type IncidentReportWithCase,
  IncidentStatus
} from '@lmc/ts-common'
import { Button, Heading, theme, Warning } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import { Modal } from 'antd'
import { postJSON } from 'common/services/api'
import { showToast } from 'common/utils'
import { NAMESPACE } from 'locale'
import { generalConstants } from 'locale/general'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useTranslation } from 'react-i18next'
import { useMutation } from 'react-query'
import {
  ConfirmationModalContent,
  ConfirmationModalTitleContainer,
  ModalFooter
} from '../display/commonStyledComponents'

type ReopenIncidentModalProps = {
  open: boolean
  setIsReopeningIncident: (value: boolean) => void
  incidentReport: IncidentReportWithCase | IncidentReport
  refetch: () => void
}
const ReopenIncidentModal = ({
  open,
  setIsReopeningIncident,
  incidentReport,
  refetch
}: ReopenIncidentModalProps) => {
  const { id: incidentReportId } = incidentReport
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)

  const updateFunction = () =>
    postJSON({
      url: `list/incident-report/${incidentReportId}/update-status`,
      method: 'PUT',
      json: { status: IncidentStatus.open }
    })

  const { mutateAsync: updateStatus, isLoading } = useMutation(
    ['incident-report', incidentReportId],
    updateFunction,
    {
      onSuccess: () => {
        showToast({
          title: t(incidentReportsConstants.INCIDENT_REOPENED)
        })
        setIsReopeningIncident(false)
        refetch()
      }
    }
  )

  const onFinish = async () => {
    try {
      await updateStatus()
    } catch {
      showToast({
        title: t(generalConstants.ERROR, { ns: NAMESPACE.GENERAL }),
        type: 'error'
      })
      setIsReopeningIncident(false)
    }
  }
  return (
    <Modal
      closable={false}
      width={546}
      open={open}
      footer={[
        <ModalFooter>
          <Button onClick={() => setIsReopeningIncident(false)}>
            {t(incidentReportsConstants.CANCEL)}
          </Button>
          <Button onClick={onFinish} isLoading={isLoading} variant="primary">
            {t(incidentReportsConstants.REOPEN_INCIDENT)}
          </Button>
        </ModalFooter>
      ]}
    >
      <ConfirmationModalContent>
        <ConfirmationModalTitleContainer>
          <Warning color={theme.color.status.warning} size={24} />
          <Heading level={4}>
            {t(incidentReportsConstants.REOPEN_MODAL_TITLE)}
          </Heading>
        </ConfirmationModalTitleContainer>
        <Divider />
        <Text>{t(incidentReportsConstants.REOPEN_MODAL_DESCRIPTION)}</Text>
        <Divider />
      </ConfirmationModalContent>
    </Modal>
  )
}

export default ReopenIncidentModal
